/**
 * B5 (E-1.C.2-05): Margin Calculator
 * Interactive: wholesale + margin → retail.
 * "With 20 clients at Growth: €X/month profit".
 * GHL comparison.
 */
import { useState } from "react";
import { useTranslation } from "@/i18n";
import { formatCurrency } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const PLAN_PRESETS = [
  { nameKey: "billing.starter", wholesaleCents: 5900, ghlEquivalentCents: 9700 },
  { nameKey: "billing.growth", wholesaleCents: 15000, ghlEquivalentCents: 49700 },
  { nameKey: "billing.scale", wholesaleCents: 29700, ghlEquivalentCents: 49700 },
];

export default function MarginCalculatorPage() {
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState(1); // Growth default
  const [marginPercent, setMarginPercent] = useState(40);
  const [clientCount, setClientCount] = useState(20);

  const preset = PLAN_PRESETS[selectedPlan];
  if (!preset) return null;

  const wholesaleEuros = preset.wholesaleCents / 100;
  const retailCents = Math.round(preset.wholesaleCents / (1 - marginPercent / 100));
  const retailEuros = retailCents / 100;
  const marginPerClient = retailEuros - wholesaleEuros;
  const monthlyProfit = marginPerClient * clientCount;
  const annualProfit = monthlyProfit * 12;
  const ghlRetailEuros = preset.ghlEquivalentCents / 100;
  const priceDiff = ghlRetailEuros - retailEuros;
  const formatEuroAmount = (euros: number) =>
    formatCurrency(Math.round(euros * 100), "EUR", { maximumFractionDigits: 0 });

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t("billing.calculator.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("billing.calculator.description")}</p>
      </div>

      {/* Plan selector */}
      <div className="flex gap-2">
        {PLAN_PRESETS.map((p, idx) => (
          <button
            key={p.nameKey}
            onClick={() => setSelectedPlan(idx)}
            className={cn("rounded-md px-4 py-2 text-sm font-medium", idx === selectedPlan ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-accent")}
          >
            {t(p.nameKey)}
          </button>
        ))}
      </div>

      {/* Inputs */}
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-sm font-medium text-foreground">{t("billing.calculator.margin")}</label>
          <div className="mt-1 flex items-center gap-3">
            <input
              type="range"
              min={10}
              max={70}
              value={marginPercent}
              onChange={(e) => setMarginPercent(Number(e.target.value))}
              className="flex-1"
            />
            <span className="w-12 text-right text-sm font-bold text-foreground">{marginPercent}%</span>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-foreground">{t("billing.calculator.clients")}</label>
          <input
            type="number"
            min={1}
            max={1000}
            value={clientCount}
            onChange={(e) => setClientCount(Number(e.target.value))}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Results */}
      <div className="rounded-lg border border-border bg-card p-6 space-y-4">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-muted-foreground">{t("billing.calculator.yourCost")}</p>
            <p className="text-xl font-bold text-foreground">{formatEuroAmount(wholesaleEuros)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t("billing.calculator.yourPrice")}</p>
            <p className="text-xl font-bold text-primary">{formatEuroAmount(retailEuros)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t("billing.calculator.profitPerClient")}</p>
            <p className="text-xl font-bold text-[var(--color-success)]">{formatEuroAmount(marginPerClient)}</p>
          </div>
        </div>

        <div className="border-t border-border pt-4 text-center">
          <p className="text-sm text-muted-foreground">
            {t("billing.calculator.projection", { clients: clientCount, plan: t(preset.nameKey) })}
          </p>
          <p className="text-3xl font-bold text-[var(--color-success)]">
            {formatEuroAmount(monthlyProfit)}
            {t("pricing.perMonth")}
          </p>
          <p className="text-sm text-muted-foreground">
            {formatCurrency(Math.round(annualProfit * 100), "EUR", { maximumFractionDigits: 0 })}
            {t("billing.calculator.perYear")}
          </p>
        </div>

        {/* GHL comparison */}
        <div className="border-t border-border pt-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase">{t("billing.calculator.ghlComparison")}</p>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{t("billing.calculator.ghlPrice")}</span>
            <span className="text-sm line-through text-muted-foreground">{formatEuroAmount(ghlRetailEuros)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">{t("billing.calculator.yourPrice")}</span>
            <span className="text-sm font-semibold text-primary">{formatEuroAmount(retailEuros)}</span>
          </div>
          {priceDiff > 0 && (
            <p className="mt-1 text-xs text-[var(--color-success)]">
              {t("billing.calculator.savings", { amount: formatEuroAmount(priceDiff) })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
